import { useState } from 'react';
import { apiClient } from 'core/utils/apiClient';
import { formatToJSTISOString, isPastDateTime } from '../utils';
import { SIZE_MAP, FONT_COLOR_MAP, BACKGROUND_COLOR_MAP, MESSAGE_MAX_LENGTH } from '../constants';
import { Hospital } from '../../hooks/useUsingDashboardHospitals';
import { useCurrentUserEmsTeam } from '../../../hooks/useCurrentUserEmsTeam';

export const useMessageSubmit = (
  resetForm: () => void,
  reloadFromAPI: () => void
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async (
    messageContent: string,
    selectedSize: string,
    selectedTextColor: string,
    selectedBgColor: string,
    selectedHospitals: string[],
    displayPeriod: Date,
    availableHospitals: Hospital[],
    emsTeam: ReturnType<typeof useCurrentUserEmsTeam>['emsTeam']
  ) => {
    if (isSubmitting) return; // 二重送信防止
    
    // 文字数バリデーション
    if (messageContent.length > MESSAGE_MAX_LENGTH) {
      console.error(`メッセージが${MESSAGE_MAX_LENGTH}文字を超えています`);
      alert(`メッセージは${MESSAGE_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    
    // 表示期限の過去日時チェック
    if (isPastDateTime(displayPeriod)) {
      alert('表示期限は現在時刻より後の時刻を設定してください');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // EMSチーム情報の確認
      if (!emsTeam?.emsTeamId) {
        throw new Error('EMSチーム情報が取得できませんでした');
      }

      // 選択された病院のIDを取得
      const targetHospitalIDs = availableHospitals
        .filter(hospital => selectedHospitals.includes(hospital.name))
        .map(hospital => hospital.id);

      const dueDate = formatToJSTISOString(displayPeriod);

      const requestData = {
        target_hospitals: targetHospitalIDs,
        message: messageContent,
        font_size: SIZE_MAP[selectedSize as keyof typeof SIZE_MAP],
        font_color: FONT_COLOR_MAP[selectedTextColor as keyof typeof FONT_COLOR_MAP],
        background_color: BACKGROUND_COLOR_MAP[selectedBgColor as keyof typeof BACKGROUND_COLOR_MAP],
        display_end_at: dueDate,
      };
      await apiClient.post(`/ems-teams/${emsTeam.emsTeamId}/messages`, requestData);
      
      // メッセージ登録成功後、履歴を更新
      reloadFromAPI();
      
      // 成功時はフォームをリセット（ダイアログは閉じない）
      resetForm();
    } catch (error) {
      console.error('メッセージ処理エラー:', error);
      
      // エラーハンドリング（必要に応じてエラーメッセージを表示）
      if (error instanceof Error) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSave
  };
}; 